/**
 * A document's reading-view facts (documents redesign cards 2.1 + 2.3): its
 * tags, who made it and when, and when it was last edited. Kept out of
 * documentsStore.js (the MCP documents surface, already large).
 *
 * - `tags`: optional `string[]` in the document record's `data` — opaque data
 *   that syncs as is; git sync Stage 2 writes it to the front matter
 *   (services/git/scopeDocumentsLayout.js), only when there are any, so a
 *   tag-less document's file stays byte-identical.
 * - `createdAt` (ms) / `createdBy` ({ id, name }): stamped by createDocument
 *   since this card; older documents have neither and the UI says less.
 * - last edited: the newer `updated_at` of the document row and its body row
 *   (`extension_records_list_meta` — `record_json` does not expose it).
 */
import { listRecordMeta, listRecords, upsertRecord } from '../../../records/recordsClient';
import { recordsContext } from './documentsStore';
import { dataOf } from './documentsScopeTree';
import { isDocDeleted } from './deletedDocsGuard';
import { aiMarkFields } from '../../../services/ai/aiAuthorship';

const EXTENSION_ID = 'documents';
export const MAX_TAGS = 12;
export const MAX_TAG_LENGTH = 40;

/** Trimmed, de-duplicated (case-insensitive), capped tag list; non-strings dropped. */
export function normalizeTags(tags) {
  const out = [];
  const seen = new Set();
  for (const raw of Array.isArray(tags) ? tags : []) {
    if (typeof raw !== 'string') continue;
    const tag = raw.replace(/\s+/g, ' ').trim().slice(0, MAX_TAG_LENGTH);
    const key = tag.toLowerCase();
    if (!tag || seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
    if (out.length >= MAX_TAGS) break;
  }
  return out;
}

async function docRows(docId, scope) {
  const ctx = recordsContext(scope);
  if (!ctx) return { ctx: null, rows: [] };
  const rows = await listRecords({ workspaceId: ctx.workspaceId, extensionId: EXTENSION_ID, recordType: null, scopeId: String(docId) });
  return { ctx, rows: rows || [] };
}

/**
 * `{ tags, createdAt, createdBy, updatedAt }` for one document, or null when it
 * is not in `scope`. `updatedAt` is null when the meta read is unavailable.
 */
export async function readDocumentMeta(docId, scope) {
  const { ctx, rows } = await docRows(docId, scope);
  const doc = rows.find((r) => String(r.id) === String(docId) && r.record_type === 'document');
  if (!ctx || !doc) return null;
  let updatedAt = null;
  try {
    const meta = (await listRecordMeta({ workspaceId: ctx.workspaceId, extensionId: EXTENSION_ID, recordType: null, scopeId: String(docId) })) || [];
    for (const m of meta) {
      // The body is its blocks (docBlocks.js) plus the docbody row.
      const own = String(m.id) === String(docId) || String(m.id) === `${docId}::body` || m.record_type === 'docblock';
      if (own && Number.isFinite(m.updated_at)) updatedAt = Math.max(updatedAt || 0, m.updated_at);
    }
  } catch {
    /* "Edited …" is a nicety; the rest of the view does not depend on it */
  }
  const by = doc.createdBy && typeof doc.createdBy === 'object' ? doc.createdBy : null;
  return {
    tags: normalizeTags(doc.tags),
    createdAt: Number.isFinite(doc.createdAt) ? doc.createdAt : null,
    createdBy: by && typeof by.name === 'string' ? { id: by.id ?? null, name: by.name } : null,
    updatedAt,
    // AI Act Art. 50 marks (services/ai/aiAuthorship.js); null on a document
    // no AI created or edited. Drives the title block's "AI" chip.
    ai: doc.ai_generated === true || doc.ai_edited_at ? aiMarkFields(doc) : null,
  };
}

/** Replaces the document's tags (an empty list removes the key). Resolves the stored list. */
export async function saveDocumentTags(docId, tags, scope) {
  if (isDocDeleted(docId)) return null;
  const { ctx, rows } = await docRows(docId, scope);
  const doc = rows.find((r) => String(r.id) === String(docId) && r.record_type === 'document');
  if (!ctx || !doc) throw new Error(`Document "${docId}" not found.`);
  const next = normalizeTags(tags);
  const data = dataOf(doc);
  if (next.length) data.tags = next;
  else delete data.tags;
  await upsertRecord({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    record: { id: doc.id, record_type: 'document', parent_id: doc.parent_id ?? null, sort_key: doc.sort_key, data },
  });
  return next;
}

/** Same tags, same order? (git apply compares before writing.) */
export const sameTags = (a, b) => JSON.stringify(normalizeTags(a)) === JSON.stringify(normalizeTags(b));
